/**
 * What the account privacy screens read and write: clinic access (ACC-031), a deletion request
 * (PRI-020, ACC-032), and the JSON files inside a data export. Type aliases rather than interfaces
 * so the Worker's `json()` accepts the payload.
 */
import { z } from "zod";

/** Engineering default in PRI-020. Deletion runs by this many days after the request. */
export const DELETION_DAYS = 30;

export function deletionDueAt(requestedAtIso: string): string {
  return new Date(Date.parse(requestedAtIso) + DELETION_DAYS * 86_400_000).toISOString();
}

export type ClinicShareScope = "clinic_created" | "all";

/**
 * Whether one document is in a clinic's share. `clinic_created` is only files that clinic uploaded
 * (`organizationId` matching). `all` is everything the patient has, including their own uploads.
 */
export function clinicSeesDocument(
  scope: ClinicShareScope,
  clinicOrganizationId: string,
  documentOrganizationId: string | null,
): boolean {
  return scope === "all" || documentOrganizationId === clinicOrganizationId;
}

export function documentsInScope<T extends { readonly organizationId: string | null }>(
  scope: ClinicShareScope,
  clinicOrganizationId: string,
  documents: readonly T[],
): readonly T[] {
  if (scope === "all") return documents;

  return documents.filter((doc) => doc.organizationId === clinicOrganizationId);
}

export type ClinicAccessStatus = "pending" | "active";

export type ClinicAccess = {
  readonly id: string;
  readonly name: string;
  readonly status: ClinicAccessStatus;
  readonly shareScope: ClinicShareScope;
  readonly createdAt: string;
};

export type ClinicAccessList = {
  readonly clinics: readonly ClinicAccess[];
};

export type DeletionState = "none" | "requested" | "completed";

export type DeletionStatus = {
  readonly status: DeletionState;
  readonly dueAt: string | null;
};

export type IntakeSave = {
  readonly saved: true;
  readonly review: boolean;
};

export type ExportAccount = {
  readonly email: string | null;
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly dateOfBirth: string | null;
  readonly sex: "female" | "male" | null;
  readonly createdAt: string;
};

export type ExportObservation = {
  readonly id: string;
  readonly biomarkerCode: string | null;
  readonly nameAsPrinted: string;
  readonly value: number | null;
  readonly unit: string | null;
  readonly flag: string | null;
  readonly collectedOn: string | null;
};

export type ExportComponent = {
  readonly name: string;
  readonly dose: number;
  readonly unit: string;
};

export type ExportFormula = {
  readonly version: number;
  readonly status: string;
  readonly origin: string;
  readonly flavor: string | null;
  readonly createdAt: string;
  readonly ingredients: readonly ExportComponent[];
};

export type ExportOrder = {
  readonly id: string;
  readonly amountCents: number;
  readonly status: string;
  readonly createdAt: string;
};

export type ExportDocument = {
  readonly id: string;
  readonly filename: string;
  readonly contentType: string;
  readonly collectedOn: string | null;
  readonly r2Key: string;
};

export type PatientExport = {
  readonly account: ExportAccount;
  readonly intake: Readonly<Record<string, string>>;
  readonly observations: readonly ExportObservation[];
  readonly formulas: readonly ExportFormula[];
  readonly orders: readonly ExportOrder[];
  readonly clinics: readonly ClinicAccess[];
  readonly documents: readonly ExportDocument[];
};

const Clinic = z.object({
  id: z.string(),
  name: z.string(),
  status: z.enum(["pending", "active"]),
  shareScope: z.enum(["clinic_created", "all"]),
  createdAt: z.string(),
});

const Clinics = z.object({ clinics: z.array(Clinic) });

const Deletion = z.object({
  status: z.enum(["none", "requested", "completed"]),
  dueAt: z.string().nullable(),
});

const Saved = z.object({ saved: z.literal(true), review: z.boolean() });

function readJson<T>(schema: z.ZodType<T>, text: string): T | null {
  try {
    const parsed = schema.safeParse(JSON.parse(text));

    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export function readClinicAccessList(text: string): ClinicAccessList | null {
  return readJson(Clinics, text);
}

const ShareBody = z.object({ shareScope: z.enum(["clinic_created", "all"]) });

export type ClinicShareBody = {
  readonly shareScope: ClinicShareScope;
};

export function readClinicShareBody(text: string): ClinicShareBody | null {
  return readJson(ShareBody, text);
}

export function readDeletionStatus(text: string): DeletionStatus | null {
  return readJson(Deletion, text);
}

export function readIntakeSave(text: string): IntakeSave | null {
  return readJson(Saved, text);
}
